import { useEffect, useState } from "react";
import { Sparkles, Download, Trash2, Save, BookOpen } from "lucide-react";
import { listBooks, genRevisionBook, getBook, updateBook, deleteBook, bookPdfUrl } from "@/lib/api";
import { toast } from "sonner";

export default function RevisionBooksTab({ subject }) {
  const [books, setBooks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setBooks(await listBooks(subject.id)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [subject.id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const b = await genRevisionBook(subject.id);
      toast.success("Revision book generated");
      await load();
      setSelected(b);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const openBook = async (id) => {
    const b = await getBook(id);
    setSelected(b);
    setDraft(b);
    setEditing(false);
  };

  const saveBook = async () => {
    try {
      const b = await updateBook(draft.id, { title: draft.title, summary: draft.summary, chapters: draft.chapters });
      setSelected(b);
      setEditing(false);
      toast.success("Saved");
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this revision book?")) return;
    await deleteBook(id);
    if (selected?.id === id) setSelected(null);
    load();
  };

  if (selected) {
    const book = editing ? draft : selected;
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <button data-testid="book-back" onClick={() => setSelected(null)} className="nb-button px-3 py-2 text-sm">← All books</button>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button onClick={() => { setEditing(false); setDraft(selected); }} className="nb-button px-3 py-2 text-sm">Cancel</button>
                <button onClick={saveBook} data-testid="book-save" className="nb-button px-3 py-2 bg-[#9DFFB0] dark:bg-[#68D391] text-sm flex items-center gap-1">
                  <Save className="w-4 h-4" /> Save
                </button>
              </>
            ) : (
              <>
                <a
                  href={bookPdfUrl(selected.id)}
                  data-testid="book-pdf-download"
                  className="nb-button px-3 py-2 bg-[#FFE066] dark:bg-[#F6E05E] text-sm flex items-center gap-1"
                >
                  <Download className="w-4 h-4" /> PDF
                </a>
                <button onClick={() => { setEditing(true); setDraft(selected); }} data-testid="book-edit" className="nb-button px-3 py-2 text-sm">Edit</button>
                <button onClick={() => remove(selected.id)} className="nb-button px-3 py-2 text-sm hover:bg-red-100">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <article className="nb-card p-6 sm:p-10 prose-lg max-w-none">
          {editing ? (
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="nb-input font-display text-3xl font-extrabold mb-4" />
          ) : (
            <h1 className="font-display text-4xl font-extrabold tracking-tight mb-3">{book.title}</h1>
          )}
          {editing ? (
            <textarea value={draft.summary || ""} onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
              className="nb-input mb-6" rows={3} />
          ) : (
            book.summary && <p className="text-lg text-zinc-700 dark:text-zinc-300 mb-6">{book.summary}</p>
          )}

          <div className="space-y-8">
            {(book.chapters || []).map((ch, ci) => (
              <section key={ci}>
                {editing ? (
                  <input
                    value={ch.heading || ""}
                    onChange={(e) => {
                      const c = [...draft.chapters];
                      c[ci] = { ...c[ci], heading: e.target.value };
                      setDraft({ ...draft, chapters: c });
                    }}
                    className="nb-input font-display text-2xl font-extrabold mb-3"
                  />
                ) : (
                  <h2 className="font-display text-3xl font-extrabold mb-4 inline-block bg-[#D4C4FB] dark:bg-[#B794F4] nb-border rounded-xl px-3 py-1 text-black">
                    {ch.heading}
                  </h2>
                )}
                <div className="space-y-5">
                  {(ch.subheadings || []).map((sub, si) => (
                    <div key={si}>
                      {editing ? (
                        <input
                          value={sub.title || ""}
                          onChange={(e) => {
                            const c = [...draft.chapters];
                            c[ci].subheadings[si] = { ...c[ci].subheadings[si], title: e.target.value };
                            setDraft({ ...draft, chapters: c });
                          }}
                          className="nb-input font-bold text-xl mb-2"
                        />
                      ) : (
                        <h3 className="font-display text-2xl font-bold mb-2">{sub.title}</h3>
                      )}
                      {(sub.bullets || []).length > 0 && (
                        <ul className="list-disc pl-6 space-y-1 mb-3">
                          {sub.bullets.map((b, i) => (
                            <li key={i} className="font-medium">
                              {editing ? (
                                <input
                                  value={b}
                                  onChange={(e) => {
                                    const c = [...draft.chapters];
                                    c[ci].subheadings[si].bullets[i] = e.target.value;
                                    setDraft({ ...draft, chapters: c });
                                  }}
                                  className="nb-input"
                                />
                              ) : b}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(sub.definitions || []).map((d, i) => (
                        <div key={i} className="nb-border rounded-xl bg-[#FFE066] dark:bg-[#F6E05E] p-3 mb-2 text-black">
                          <span className="font-extrabold">{d.term}:</span> <span className="font-medium">{d.definition}</span>
                        </div>
                      ))}
                      {(sub.key_concepts || []).map((k, i) => (
                        <div key={i} className="nb-border rounded-xl bg-[#9DFFB0] dark:bg-[#68D391] p-3 mb-2 text-black">
                          <span className="label-caps">Key</span><br />
                          <span className="font-bold">{k}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <p className="text-zinc-600 dark:text-zinc-400 font-medium">
          AI-summarized revision books generated from your notes.
        </p>
        <button
          data-testid="generate-book-btn"
          onClick={generate}
          disabled={generating}
          className="nb-button px-5 py-3 bg-[#D4C4FB] dark:bg-[#B794F4] flex items-center gap-2"
        >
          <Sparkles className="w-5 h-5" strokeWidth={3} />
          {generating ? "Generating…" : "Generate Book"}
        </button>
      </div>

      {loading ? (
        <div className="font-bold text-zinc-500">Loading…</div>
      ) : books.length === 0 ? (
        <div className="nb-card p-8 text-center">
          <BookOpen className="w-10 h-10 mx-auto mb-2" />
          <div className="font-display text-2xl font-extrabold mb-1">No books yet</div>
          <p className="text-zinc-600 dark:text-zinc-400">Generate one from your uploaded notes.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {books.map((b) => (
            <button
              key={b.id}
              onClick={() => openBook(b.id)}
              data-testid={`book-card-${b.id}`}
              className="nb-card p-5 text-left"
            >
              <div className="w-10 h-10 rounded-lg nb-border bg-[#D4C4FB] dark:bg-[#B794F4] flex items-center justify-center mb-3">
                <BookOpen className="w-5 h-5 text-black" strokeWidth={2.5} />
              </div>
              <div className="font-display text-xl font-extrabold mb-1 line-clamp-2">{b.title}</div>
              <div className="text-xs font-bold text-zinc-500">
                {b.chapters?.length || 0} chapters
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
